import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRouter } from "next/router";
import Link from "next/link";
import { App, Host, JSONSafe, ProviderType, Task, TaskArgsType } from "@/types";
import { Template } from "@/types/template";
import {
  cn,
  fetcherTyped,
  getIpAddress,
  getWildcardDomain,
  interpolateValue,
  isFileSystemSafe,
  okstatus,
  str
} from "@/lib/utils";
import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";

import { useLatestJournal } from "@/components/dashboard/useLatestJournal";
import { ExternalLink, ObjectLink } from "@/components/OptionalLink";
import { Stepper } from "@/components/Stepper";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";
import { getProviderMeta } from "@/lib/provider/meta";
import { GlowBorder } from "@/components/GlowBorder";
import { formatMarkdown } from "@/components/formatMarkdown";
import dynamic from "next/dynamic";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTaskStatus } from "@/components/task/useTaskStatus";
import logger from "@/lib/logger";
import { AppSchema } from "@/types/schemas";

const AnimatedBeamMultipleOutputDemo = dynamic(
  () => import("@/components/AnimatedBeamMultipleInputs"),
  { ssr: false }
);

export const Launch = ({
  template,
  onNext
}: {
  template: Template;
  onNext?: (
    step: "host" | "provision" | "existing" | "launch",
    args?: Omit<TaskArgsType, "deleteApp" | "deleteHost" | "message">
  ) => void;
}) => {
  const router = useRouter();
  const { data: task, mutate } = useSWR<JSONSafe<Task>>(
    router.query.taskId ? `/api/task/${router.query.taskId}` : null
  );
  const { latest } = useLatestJournal(!!task?.id, task?.id, 1000, 100);
  const [launching, setLaunching] = useState(false);
  const [retry, setRetry] = useState(false);
  const hostId = str(router.query.hostId);
  const hostname = str(router.query.hostname);
  const sshKeyId = str(router.query.sshKeyId);
  const username = str(router.query.username);
  const providerType = (router.query.provider as ProviderType) ?? "do";
  const { data: host } = useSWR<JSONSafe<Host>>(hostId ? `/api/host/${hostId}` : null);
  const { data: app } = useSWR(
    task?.appId ? `/api/app/${task.appId}` : null,
    fetcherTyped(AppSchema)
  );
  const meta = getProviderMeta(providerType);
  const [ipAddress, setIpAddress] = useState<string | undefined>(undefined);
  const [appName, setAppName] = useState<string>(template?.id || "");
  const { data: existingApps } = useSWR<JSONSafe<App[]>>("/api/app");
  useTaskStatus(
    task?.id,
    500,
    router.query.taskId ? [`/api/task/${router.query.taskId}`] : undefined
  );
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (template.app?.allowAccessFrom === "${USER_IPADDRESS}") {
      getIpAddress().then(setIpAddress);
    }
  }, [template.app?.allowAccessFrom]);

  const handleCancel = useCallback(
    async (taskId?: string) => {
      if (!taskId) {
        return Promise.resolve();
      }

      try {
        await fetch(`/api/task/${taskId}/cancel`, {
          method: "POST"
        }).then(okstatus);
      } catch (error) {
        mutate();
        logger.warn(error);
      }

      setCancelled(true);
      let executing = false;
      do {
        const result = await fetch(`/api/task/${taskId}`)
          .then(okstatus)
          .then(r => r.json() as Promise<JSONSafe<Task>>);
        executing = result.executing ?? false;
        mutate(result, { revalidate: false });
        await new Promise(resolve => setTimeout(resolve, 500));
      } while (executing);
    },
    [mutate]
  );

  const handleLaunch = useCallback(() => {
    setLaunching(true);
    fetch("/api/task/launch", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        templateId: template?.id,
        ipAddress,
        appName,
        ...// Existing host
        (str(router.query.hostId)
          ? { hostId: str(router.query.hostId), hostname: str(router.query.hostname) }
          : // Add new existing host to DD
            str(router.query.hostname)
            ? {
                hostname: str(router.query.hostname),
                username: str(router.query.username),
                sshKeyId: str(router.query.sshKeyId)
              }
            : {
                providerType: providerType,
                instanceType: str(router.query.providerType),
                regionId: str(router.query.regionId),
                sshKeyId: str(router.query.sshKeyId),
                hostname: str(router.query.hostname)
              })
      })
    })
      .then(okstatus)
      .then(r => r.json() as Promise<Task>)
      .then((task: Task) =>
        router.replace(
          `/r/${template.id}/launch?${new URLSearchParams({
            ...router.query,
            taskId: task.id
          }).toString()}`,
          undefined,
          { shallow: true }
        )
      )
      .finally(() => setLaunching(false));
  }, [template?.id, router, providerType, ipAddress, appName]);

  const handleRetry = useCallback(
    (force?: boolean) => {
      if (task) {
        setRetry(true);
        fetch(`/api/task/${task?.id}/queue`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ force })
        })
          .then(okstatus)
          .then(() => mutate())
          .finally(() => setRetry(false));
      }
    },
    [mutate, task]
  );

  return (
    <div className="flex flex-col justify-center items-center w-full min-h-screen p-6">
      <h2 className="text-2xl font-bold">{template?.name}</h2>
      <div className="my-4">
        <img src={template?.logo} alt={template?.name} className="h-12 rounded-md" />
      </div>

      <h2 className="mt-2 text-md font-bold">Launch your app</h2>

      {task ? null : (
        <div className="mt-6 flex flex-col md:flex-row gap-6 mb-8">
          <Card className="md:w-[500px]">
            <CardHeader className="justify-center items-center">
              <CardTitle className="text-center text-xl">Ready for launch!</CardTitle>
              {hostId || hostname ? (
                <CardDescription className="text-center">
                  Your app is ready to be deployed to {hostId ? "existing" : "new"} host.
                </CardDescription>
              ) : (
                <CardDescription className="text-center">
                  <p>Your VPS is ready to be created.</p>
                  <p>
                    This takes {template.deployTime ?? "couple minutes"} + time to prepare the
                    host.
                  </p>
                </CardDescription>
              )}
            </CardHeader>

            <CardContent className="flex flex-col gap-4 max-w-[500px] shrink-0">
              <div className="w-full mb-4 flex flex-col gap-2">
                <Label htmlFor="customName">App name</Label>
                <div className="flex flex-col gap-2">
                  <Input
                    id="customName"
                    value={appName}
                    onChange={e => setAppName(e.target.value)}
                    placeholder="Enter a custom name for your app"
                    className={`w-full ${appName.trim() === "" ? "focus-visible:ring-red-500" : ""}`}
                  />
                  {hostId || hostname ? null : (
                    <span className="text-xs text-muted-foreground">
                      Deployed as https://{appName}.&lt;random&gt;.{getWildcardDomain()}
                    </span>
                  )}
                </div>
                {appName.trim() !== "" && existingApps?.some(a => a.name === appName) ? (
                  <p className="text-xs text-destructive">
                    App name {appName} is already in use.
                  </p>
                ) : appName.trim() === "" ? (
                  <p className="text-xs text-destructive">Cannot be empty</p>
                ) : !isFileSystemSafe(appName.trim()) ? (
                  <p className="text-xs text-destructive">
                    Should be only letters, numbers and &quot;-&quot;.
                  </p>
                ) : hostId || hostname ? (
                  <p className="text-xs text-muted-foreground">
                    Identifies the app and the app folder on the host.
                  </p>
                ) : null}
              </div>

              {hostId ? (
                <>
                  <div>
                    <AnimatedBeamMultipleOutputDemo
                      className="w-full p-0"
                      template={template}
                      hostname={appName || hostname}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4 w-[250px] md:w-[450px]">
                    <div className="text-left flex flex-col">
                      <div className="font-bold">Dollar Deploy</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="font-bold">Host: </div>
                      <div className="text-sm">{host?.ipAddress ?? hostId}</div>
                    </div>
                    <div className="flex flex-col items-end">
                      <div className="text-right font-bold">{template?.name}</div>
                    </div>
                  </div>
                  <div className="mt-10 text-center flex flex-col">
                    <span>App will be ready at:</span>
                    <Link
                      href={"https://" + hostname}
                      className="link"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {hostname}
                    </Link>
                  </div>
                </>
              ) : hostname ? (
                <>
                  <div>
                    <AnimatedBeamMultipleOutputDemo
                      className="w-full p-0"
                      template={template}
                      hostname={hostname}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4 w-[250px] md:w-[450px]">
                    <div className="text-left flex flex-col">
                      <div className="font-bold">Dollar Deploy</div>
                    </div>
                    <div className="items-center flex flex-col">
                      <div className="font-bold">Host: </div>
                      <div className="text-sm">{hostname}</div>
                    </div>
                    <div className="flex flex-col items-end">
                      <div className="text-right font-bold">{template?.name}</div>
                    </div>
                  </div>

                  <div className="mt-10 text-center flex flex-col">
                    <span>App will be ready at:</span>
                    <Link
                      href={"https://" + hostname}
                      className="link"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {hostname}
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <AnimatedBeamMultipleOutputDemo
                      className="w-full p-0"
                      template={template}
                      hostname={appName}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4 w-full">
                    <div className="text-left flex flex-col">
                      <div className="font-bold">Dollar Deploy</div>
                    </div>
                    <div className="text-center flex flex-col">
                      <div className="font-bold">{meta?.name}</div>
                      <div className="text-sm">{router.query.regionId}</div>
                      <div className="text-sm">{router.query.providerType}</div>
                    </div>
                    <div className="flex flex-col items-end">
                      <div className="text-right font-bold">{template?.name}</div>
                    </div>
                  </div>
                </>
              )}

              {ipAddress ? (
                <div className="border rounded-md p-4">
                  <b>This template launches app without authentication.</b> We will restrict
                  access to your IP address ({ipAddress}).
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}

      {task ? (
        <div className="mt-6 flex flex-col gap-6 mb-8 items-center">
          <GlowBorder loading={task.status === "active"} className="w-[320px] md:w-[500px]">
            <Card
              className={cn(
                "w-[320px] md:w-[500px] py-0 rounded-sm",
                task.status === "active" ? "border-none" : "border"
              )}
            >
              <CardContent className="bg-black h-[280px] overflow-y-auto p-4 rounded-sm">
                {latest?.map(l => (
                  <div
                    key={l.id}
                    className="line-clamp-1 text-sm text-secondary/80 dark:text-secondary-foreground/80 break-all"
                  >
                    {l.message}
                  </div>
                ))}
              </CardContent>
            </Card>
          </GlowBorder>

          {task.status === "active" || task.status === "cancelled" ? (
            <div className="flex items-center gap-2">
              <span className="text-md font-medium text-accent-foreground/70">
                {task.status === "cancelled" ? "Cancelling..." : "Launching your app..."}
              </span>

              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild disabled={task.status === "cancelled"}>
                  <Button
                    aria-haspopup="true"
                    size="icon"
                    variant="outline"
                    className="h-8"
                    animations="none"
                  >
                    <MoreVertical className="h-4 w-4" />
                    <span className="sr-only">Toggle menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => handleCancel(task.id)}
                    disabled={task.status === "cancelled"}
                  >
                    Cancel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : task?.status === "error" ? (
            <div className="flex flex-col items-center gap-4">
              <div className="text-destructive text-center">
                {cancelled
                  ? "Launch was cancelled. Some resources might be left running. "
                  : null}
                {JSON.parse(task.result ?? "{}")?.error}
              </div>
              <Button size="sm" onClick={() => handleRetry(false)} disabled={retry}>
                Retry
              </Button>
            </div>
          ) : task?.status === "completed" ? (
            <div className="flex flex-col gap-4">
              {template.postLaunchNote && app ? (
                <div className="border rounded-md p-4 w-full md:w-[500px]">
                  {formatMarkdown(
                    interpolateValue(template.postLaunchNote, {
                      ...(app?.env ?? {}),
                      ...(app?.hostname
                        ? { APP_URL: "https://" + app.hostname, APP_HOSTNAME: app.hostname }
                        : {})
                    }) ?? ""
                  )}
                </div>
              ) : null}

              <div className="text-green-500 flex flex-col gap-2 items-center justify-center">
                <div className="text-center mb-2">Deployment completed successfully</div>
                <div className="flex flex-row gap-2 items-center justify-center">
                  {app?.hostname ? (
                    <Button asChild size="sm" className="h-8">
                      <ExternalLink href={"https://" + app.hostname}>Open</ExternalLink>
                    </Button>
                  ) : null}

                  {task?.appId ? (
                    <Button size="sm" variant="secondary" className="h-8" asChild>
                      <ObjectLink type="app" href={`/app/${task.appId}`}>
                        App details
                      </ObjectLink>
                    </Button>
                  ) : null}

                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        aria-haspopup="true"
                        size="icon"
                        variant="secondary"
                        className="h-8"
                        animations="none"
                      >
                        <MoreVertical className="h-4 w-4" />
                        <span className="sr-only">Toggle menu</span>
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          handleRetry(true);
                        }}
                      >
                        Redeploy
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <Button
          size="lg"
          disabled={
            launching ||
            appName?.trim() === "" ||
            existingApps?.some(a => a.name === appName) ||
            !isFileSystemSafe(appName.trim())
          }
          onClick={e => {
            e.preventDefault();
            handleLaunch();
          }}
        >
          Commence
        </Button>
      )}
      <div className="mt-8">
        <Stepper
          steps={3}
          current={2}
          onStep={step => {
            if (step === 0) {
              onNext && onNext("host");
            } else if (step === 1) {
              onNext &&
                onNext(hostname || hostId ? "existing" : "provision", {
                  providerType,
                  hostId,
                  hostname,
                  username,
                  sshKeyId
                });
            }
          }}
        />
      </div>
    </div>
  );
};
